import React, { useEffect, useState } from 'react';
import { Badge, Tooltip } from '@mantine/core';
import { IconClock, IconAlertTriangle } from '@tabler/icons-react';

// Blinking Badge Component - Platform Element
export const BlinkingBadge = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
    blinkSpeed?: number;
    [key: string]: any;
  }
>(({ children, blinkSpeed = 1000, ...badgeProps }, ref) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(prev => !prev);
    }, blinkSpeed);

    return () => clearInterval(interval);
  }, [blinkSpeed]);

  return (
    <Badge
      ref={ref}
      {...badgeProps}
      style={{
        ...badgeProps.style,
        opacity: isVisible ? 1 : 0.3,
        transition: 'opacity 0.1s ease',
      }}
    >
      {children}
    </Badge>
  );
});

BlinkingBadge.displayName = 'BlinkingBadge';

// Predefined Blinking Badge Components for Common Use Cases
export const BlinkingOverdueBadge = ({ count }: { count: number }) => (
  <Tooltip label={`🚨 ${count} OVERDUE items - URGENT ACTION REQUIRED! 🚨`}>
    <BlinkingBadge
      size='xs'
      color='red'
      variant='filled'
      leftSection={<IconClock size={10} />}
      blinkSpeed={500} // Fast blinking for urgency!
    >
      {count}
    </BlinkingBadge>
  </Tooltip>
);

export const BlinkingHotBadge = ({ count }: { count: number }) => (
  <Tooltip label={`🔥 ${count} HOT items - Don't let them get cold! 🔥`}>
    <BlinkingBadge
      size='xs'
      color='orange'
      variant='gradient'
      gradient={{ from: 'red', to: 'orange' }}
      leftSection={<IconAlertTriangle size={10} />}
      blinkSpeed={800} // Medium blinking
    >
      {count}
    </BlinkingBadge>
  </Tooltip>
);

export const BlinkingNewBadge = ({ count }: { count: number }) => (
  <Tooltip label={`✨ ${count} fresh new items waiting for you! ✨`}>
    <BlinkingBadge
      size='xs'
      color='blue'
      variant='filled'
      blinkSpeed={1200} // Slow, gentle blinking
    >
      {count}
    </BlinkingBadge>
  </Tooltip>
);

export const BlinkingCriticalBadge = ({ count }: { count: number }) => (
  <Tooltip label={`⚠️ ${count} CRITICAL items - Immediate attention needed! ⚠️`}>
    <BlinkingBadge
      size='xs'
      color='red'
      variant='gradient'
      gradient={{ from: 'red', to: 'pink' }}
      leftSection={<IconAlertTriangle size={10} />}
      blinkSpeed={300} // Very fast blinking for critical items!
    >
      {count}
    </BlinkingBadge>
  </Tooltip>
);

export const BlinkingInfoBadge = ({ count }: { count: number }) => (
  <Tooltip label={`ℹ️ ${count} items need your attention`}>
    <BlinkingBadge
      size='xs'
      color='cyan'
      variant='light'
      blinkSpeed={1500} // Very gentle blinking for info
    >
      {count}
    </BlinkingBadge>
  </Tooltip>
);
